import React, { useState, useEffect } from "react";
import { FaTrash, FaPlus } from "react-icons/fa";
import { useDeleteItem } from "../../_helper/actions/mutation/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { toast } from "react-toastify";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { useParams } from "react-router-dom";
import { useAtom } from "jotai";
import { clinicAtom } from "../../_helper/constant/constant-atom";
import Select from "react-select";

const BoxList = ({
  boxes,
  rackIndex,
  shelfIndex,
  racks,
  setRacks,
  isEditMode,
  generateName,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [searchInput, setSearchInput] = useState(null);
  const [isMedicine, setIsMedicine] = useState(true);
  const [clinic] = useAtom(clinicAtom);

  const { id } = useParams("");

  const dlt_mutation = useDeleteItem(ApiEndPoint.PHARMACY);

  const [combinedOptions, setCombinedOptions] = useState([]);

  console.log(combinedOptions, "Combination Options");

  const { data: itemsData, isLoading: isLoadingItem } = useItemsFilter(
    ApiEndPoint.INVENTORY,
    `?action=getItem&items_name=${searchInput}&pharmacy_id=${id}`,
    searchInput ? true : false
  );

  const { data: medicineData, isLoading: isLoadingMedicine } = useItemsFilter(
    ApiEndPoint.SEARCH_MEDICINE_DATA,
    `?medicine_name=${searchInput}`,
    itemsData?.length === 0 // Only fetch medicine data if no item data is found
  );

  useEffect(() => {
    if (itemsData?.length > 0) {
      // If isMedicine is false, only show item options
      const itemOptions = itemsData.map((item) => ({
        value: item.id,
        label: item.name,
        isMedicine: false, // Explicitly mark item data as not medicine
      }));
      setCombinedOptions(itemOptions);
    } else if (itemsData?.length === 0 && medicineData) {
      const medicineOptions = medicineData.map((item) => ({
        isMedicine: true, // Explicitly mark medicine data as medicine
        value: item.id,
        label: item.medicine_name,
        medicine_name: item.medicine_name,
        generic_name: medicineData.generic_name,
        __isNew__: true, // Explicitly mark medicine data items as new
      }));
      setCombinedOptions([...medicineOptions]);
    }
  }, [itemsData, medicineData, isMedicine]);

  // Add existing items to combinedOptions if they are not already present
  useEffect(() => {
    const existingItems = boxes?.flatMap((box) =>
      box?.items
        ?.filter((item) => item.id && item.items_name) // Only include items with valid id and name
        ?.map((item) => ({
          value: item.id,
          label: item.items_name,
          isMedicine: item.isMedicine || false, // Include isMedicine flag for existing items
        }))
    );

    const uniqueExistingItems = existingItems.filter(
      (item) => !combinedOptions.some((option) => option.value === item.value)
    );

    if (uniqueExistingItems.length > 0) {
      setCombinedOptions((prevOptions) => [
        ...prevOptions,
        ...uniqueExistingItems,
      ]);
    }
  }, [boxes]);

  const addBox = () => {
    const updatedRacks = [...racks];
    const newBox = {
      box_name: generateName(
        updatedRacks[rackIndex].shelves[shelfIndex].boxes.length,
        "Box"
      ),
      items: [], // Initialize items as an empty array
    };
    updatedRacks[rackIndex].shelves[shelfIndex].boxes.push(newBox);
    setRacks(updatedRacks);
  };

  const addItemToBox = (boxIndex) => {
    const updatedRacks = [...racks];
    const newItem = { id: "", items_name: "", isMedicine: false }; // Add default item with empty id and items_name
    if (!updatedRacks[rackIndex].shelves[shelfIndex].boxes[boxIndex].items) {
      updatedRacks[rackIndex].shelves[shelfIndex].boxes[boxIndex].items = []; // Ensure `items` is initialized
    }
    updatedRacks[rackIndex].shelves[shelfIndex].boxes[boxIndex].items.push(
      newItem
    );
    setRacks(updatedRacks);
  };

  const updateItemInBox = (boxIndex, itemIndex, selectedOption) => {
    if (!selectedOption) {
      // Handle clearing the selection
      const updatedRacks = [...racks];
      updatedRacks[rackIndex].shelves[shelfIndex].boxes[boxIndex].items[
        itemIndex
      ] = {
        id: "", // Reset ID
        items_name: "", // Reset name
        isMedicine: false, // Reset medicine flag
      };
      setRacks(updatedRacks);
      return;
    }

    const updatedRacks = [...racks];
    const isMedicineItem = selectedOption.isMedicine || false;

    updatedRacks[rackIndex].shelves[shelfIndex].boxes[boxIndex].items[
      itemIndex
    ] = {
      id: isMedicineItem ? null : selectedOption.value, // Exclude ID if it's a medicine item
      items_name: selectedOption.label,
      isMedicine: isMedicineItem, // Set isMedicine flag based on the selected option
    };

    setRacks(updatedRacks);
  };

  const deleteItemFromBox = (boxIndex, itemIndex) => {
    const updatedRacks = [...racks];
    updatedRacks[rackIndex].shelves[shelfIndex].boxes[boxIndex].items.splice(
      itemIndex,
      1
    );
    setRacks(updatedRacks);
  };

  const handleDeleteClick = (boxIndex, id) => {
    setConfirmDelete({ boxIndex: boxIndex, id: id });
  };

  const confirmDeleteBox = (boxIndex) => {
    if (boxIndex.id) {
      dlt_mutation.mutate(
        { data: { action: "delBox", id: boxIndex.id } },
        {
          onSuccess: () => {
            toast.success("Box deleted!");
          },
        }
      );
    }
    const updatedRacks = [...racks];
    updatedRacks[rackIndex].shelves[shelfIndex].boxes = updatedRacks[
      rackIndex
    ].shelves[shelfIndex].boxes.filter((_, i) => i !== boxIndex.boxIndex);
    setRacks(updatedRacks);
    setConfirmDelete(null);
  };

  const cancelDelete = () => {
    setConfirmDelete(null);
  };

  const handleInputChange = (inputValue) => {
    // Update the search input state with the text the user is typing
    setSearchInput(inputValue);
  };

  return (
    <div className="container mx-auto px-4">
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-6">
        {boxes?.map((box, boxIndex) => (
          <div
            key={boxIndex}
            className="p-5 bg-white rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-lg font-semibold text-gray-900">
                {box.box_name}
              </h5>
              {isEditMode && (
                <button
                  className="text-red-600 hover:text-red-800"
                  onClick={() => handleDeleteClick(boxIndex, box.id)}
                >
                  <FaTrash />
                </button>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Items in Box
              </label>
              <ul className="space-y-2">
                {box?.items?.map((item, itemIndex) => (
                  <li key={itemIndex} className="relative flex flex-col gap-3">
                    {isEditMode ? (
                      <>
                        <Select
                          isLoading={isLoadingMedicine || isLoadingItem}
                          options={combinedOptions}
                          value={
                            item.items_name
                              ? {
                                  value: item.id || null, // Use null for medicine items
                                  label: item.items_name,
                                }
                              : null
                          }
                          onChange={(selectedOption) => {
                            updateItemInBox(
                              boxIndex,
                              itemIndex,
                              selectedOption
                            );
                          }}
                          isSearchable
                          isClearable
                          placeholder="Select an item"
                          className="w-full"
                          classNamePrefix="react-select"
                          onInputChange={handleInputChange} // Capture the text the user is typing
                        />

                        <button
                          className="text-red-500 hover:text-red-700 mt-1"
                          onClick={() => deleteItemFromBox(boxIndex, itemIndex)}
                        >
                          <FaTrash />
                        </button>
                      </>
                    ) : (
                      <p className="text-gray-700">
                        ID: {item.id || "N/A"}, Name: {item.items_name || "N/A"}
                        , Medicine: {item.isMedicine ? "Yes" : "No"}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
              {isEditMode && (
                <button
                  onClick={() => addItemToBox(boxIndex)}
                  className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  + Add Item
                </button>
              )}
            </div>
          </div>
        ))}
        {isEditMode && (
          <button
            className="flex items-center justify-center p-4 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 transition duration-300"
            onClick={addBox}
          >
            <FaPlus className="mr-2" />
            Add Box
          </button>
        )}
      </div>

      {confirmDelete !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl text-center">
            <p className="text-lg font-semibold text-gray-800">
              Are you sure you want to delete this box?
            </p>
            <div className="mt-4 flex justify-center gap-4">
              <button
                onClick={() => confirmDeleteBox(confirmDelete)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Yes
              </button>
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoxList;
